import { Controller } from "@hotwired/stimulus"

// The in-the-moment verification experience on a contact page
// (Email_Verification_Feedback_Plan.md, A). The page waits for Hunter.io's
// answer by polling the SAVED status (never the socket: a broadcast that
// lands before the page has re-subscribed is lost), repaints the pill, and
// only an invalid verdict interrupts, with a native modal: fix the address
// and check again, keep it with a reason (senior staff), or leave it. The
// red bar under the header carries a failure until it is fixed or kept.
export default class extends Controller {
  static targets = ["pill", "pillWord", "modal", "message", "fixBlock", "email", "reasonBlock", "reason",
                    "error", "checkBtn", "keepBtn", "bar", "barEmail"]
  static values = {
    url: String, fixUrl: String, keepUrl: String, contactId: Number,
    checking: Boolean, openNow: Boolean
  }

  connect() {
    document.body.dataset.watchingContactId = String(this.contactIdValue)
    // A verdict that lands after the poller gave up (a Hunter 429 pushes a
    // check 5 to 15 minutes out) arrives as a swallowed toast; the toast
    // controller re-dispatches it as this event and the page catches up.
    this.onVerdict = (e) => { if (String(e.detail?.contact_id) === String(this.contactIdValue)) this.fetchStatus() }
    document.addEventListener("email-verification:verdict", this.onVerdict)
    if (this.openNowValue) this.openModal()
    if (this.checkingValue) this.startPolling()
  }

  disconnect() {
    this.stopPolling()
    document.removeEventListener("email-verification:verdict", this.onVerdict)
    delete document.body.dataset.watchingContactId
  }

  // --- polling -------------------------------------------------------------

  startPolling(onSettled) {
    this.stopPolling()
    this.polls = 0
    this.generation = (this.generation || 0) + 1
    this.onSettled = onSettled
    this.timer = setInterval(() => this.poll(), 1500)
  }

  stopPolling() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }

  // The scheduled poll: 30 s at 1.5 s, then every 5 s, about 3 minutes in
  // all. A response from an older round (a slow fetch overlapping a restart)
  // is dropped, so the modal never opens twice.
  async poll() {
    this.polls += 1
    if (this.polls === 21) { clearInterval(this.timer); this.timer = setInterval(() => this.poll(), 5000) }
    if (this.polls > 51) { this.stopPolling(); this.settle({ timedOut: true }); return }
    await this.fetchStatus(this.generation)
  }

  // One read of the saved status; settles the page when the verdict is in.
  async fetchStatus(generation = this.generation) {
    try {
      const res = await fetch(this.urlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!res.ok) return
      const data = await res.json()
      if (generation !== this.generation) return
      if (data.checking) return
      this.stopPolling()
      this.settle(data)
    } catch (e) {
      // A missed poll is nothing; the next one catches up.
    }
  }

  settle(data) {
    if (data.timedOut) {
      if (this.modalOpen()) {
        this.setMessage("Still checking. You can close this; the result will show on this page and in Tuesday's email.")
        this.busy(false)
      }
      return
    }
    this.paint(data)
    if (this.onSettled) { const cb = this.onSettled; this.onSettled = null; cb(data); return }
    if (data.status === "invalid" && !data.kept) this.openModal()
  }

  // --- the pill and the bar ---------------------------------------------

  paint(data) {
    if (this.hasPillWordTarget) this.pillWordTarget.textContent = data.word
    if (this.hasPillTarget) {
      // A pill may carry its own tone classes (the SPT curator page, whose
      // layout has no Tailwind); the PRT contact page uses the defaults.
      let tones = { green: "bg-[var(--color-status-confirmed)]/20 text-[var(--color-status-confirmed)]",
                    red: "bg-[var(--color-status-declined)]/20 text-[var(--color-status-declined)]",
                    amber: "bg-[var(--color-status-pending)]/20 text-[var(--color-status-pending)]",
                    grey: "bg-[var(--color-mc-bg)] text-[var(--color-mc-text-secondary)]" }
      let base = "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium "
      if (this.pillTarget.dataset.toneClasses) {
        try { tones = JSON.parse(this.pillTarget.dataset.toneClasses); base = "" } catch (_e) { /* keep the defaults */ }
      }
      this.pillTarget.className = `${base}${tones[data.tone] || ""}`
      this.pillTarget.dataset.tone = data.tone || ""
      this.pillTarget.querySelector("svg")?.remove()
    }
    if (this.hasBarTarget) {
      const failing = data.status === "invalid" && !data.kept
      this.barTarget.hidden = !failing
      if (this.hasBarEmailTarget && data.email) this.barEmailTarget.textContent = data.email
    }
  }

  // --- the modal -----------------------------------------------------------

  openModal() {
    if (!this.hasModalTarget || this.modalOpen()) return
    this.modalTarget.style.display = ""
    document.body.classList.add("overflow-hidden")
    this.showError("")
    if (this.hasReasonBlockTarget) this.reasonBlockTarget.hidden = true
    if (this.hasFixBlockTarget) this.fixBlockTarget.hidden = false
    if (this.hasKeepBtnTarget) { this.keepBtnTarget.textContent = "Keep it anyway"; this.keepBtnTarget.hidden = false }
    if (this.hasCheckBtnTarget) { this.checkBtnTarget.textContent = "Check again"; this.checkBtnTarget.dataset.action = "email-verification#checkAgain" }
    this.keydown = (e) => { if (e.key === "Escape") this.leave() }
    document.addEventListener("keydown", this.keydown)
    if (this.hasEmailTarget) { this.emailTarget.focus(); const v = this.emailTarget.value; this.emailTarget.setSelectionRange(v.length, v.length) }
  }

  leave(event) {
    event?.preventDefault()
    if (!this.hasModalTarget) return
    this.modalTarget.style.display = "none"
    document.body.classList.remove("overflow-hidden")
    if (this.keydown) document.removeEventListener("keydown", this.keydown)
    if (this.reloadOnClose) window.location.reload()
  }

  modalOpen() {
    return this.hasModalTarget && this.modalTarget.style.display !== "none"
  }

  async checkAgain(event) {
    event?.preventDefault()
    const address = this.hasEmailTarget ? this.emailTarget.value.trim() : ""
    if (!address) { this.showError("Type the address first."); return }
    this.busy(true)
    this.showError("")
    const res = await this.send("PATCH", this.fixUrlValue, { email: address })
    if (!res.ok) { this.showError(res.message || "That address could not be saved."); this.busy(false); return }
    this.setMessage("Checking the new address...")
    this.startPolling((data) => {
      this.busy(false)
      if (data.status === "invalid" && !data.kept) {
        this.setMessage(`Checked twice. Hunter.io still says ${data.email} doesn't work. Look for a typo, or keep it if you are sure.`)
      } else if (data.status === "verified" || data.status === "catch_all") {
        this.setMessage(data.status === "verified"
          ? "That one works. Sending is on."
          : "Can't confirm this one: the mail server accepts everything, so it will be sent, but bounces are possible.")
        this.checkBtnTarget.textContent = "Done"
        this.checkBtnTarget.dataset.action = "email-verification#leave"
        if (this.hasFixBlockTarget) this.fixBlockTarget.hidden = true
        if (this.hasKeepBtnTarget) this.keepBtnTarget.hidden = true
        this.reloadOnClose = true
      } else {
        this.setMessage("Couldn't check this address right now. TEAM will check it again before any send.")
        this.reloadOnClose = true
      }
    })
  }

  async keepAnyway(event) {
    event?.preventDefault()
    if (!this.hasReasonBlockTarget) return
    if (this.reasonBlockTarget.hidden) {
      this.reasonBlockTarget.hidden = false
      this.reasonTarget.focus()
      this.keepBtnTarget.textContent = "Keep it with this reason"
      return
    }
    const reason = this.reasonTarget.value.trim()
    if (!reason) { this.showError("Say why in a few words; it shows on the contact."); this.reasonTarget.focus(); return }
    this.busy(true)
    const res = await this.send("POST", this.keepUrlValue, { reason })
    this.busy(false)
    if (!res.ok) { this.showError(res.message || "Could not keep this address."); return }
    this.paint(res)
    this.reloadOnClose = true
    this.leave()
  }

  // --- helpers -------------------------------------------------------------

  async send(method, url, body) {
    try {
      const res = await fetch(url, {
        method, credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrf() },
        body: JSON.stringify(body)
      })
      const data = await res.json().catch(() => ({}))
      return { ok: res.ok && data.ok !== false, ...data }
    } catch (e) {
      return { ok: false, message: "The connection dropped. Try again." }
    }
  }

  csrf() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  setMessage(text) {
    if (this.hasMessageTarget) this.messageTarget.textContent = text
  }

  showError(text) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = text
    this.errorTarget.hidden = !text
  }

  busy(on) {
    if (this.hasCheckBtnTarget) { this.checkBtnTarget.disabled = on; if (on) this.checkBtnTarget.textContent = "Checking..."; else if (this.checkBtnTarget.textContent === "Checking...") this.checkBtnTarget.textContent = "Check again" }
    if (this.hasKeepBtnTarget) this.keepBtnTarget.disabled = on
  }
}
